import React, { useCallback, useState } from 'react';
import { LayoutPage, LayoutContent, SaveAndCompleteLaterButton, YellowMessage } from 'components';
import { Container, Text, Stack, Flex, Message } from 'basis';
import { useStoreValue } from 'store';
import { SessionTimeout } from 'components/session-timeout/SessionTimeout';
import { useWaitAndRedirect, useResendBiometricsSms } from 'hooks';
import { config } from '_config';
import { getDataLayerElements, isDJ, isAU, byCountry } from 'utils';
import styled from '@emotion/styled';
import { ShadowedContainer } from 'components/info-card/InformationCard';
import { SuccessMessage } from 'components/message/SuccessMessage';
import BioIllustration from 'images/biometric-verification.svg';
import IconId from 'images/icons/id.svg';
import IconMobile from 'images/icons/mobile.svg';
import IconMessage from 'images/icons/message.svg';
import IconTimer from 'images/icons/timer.svg';
import { useEventTracking } from 'react-event-tracker';

export const ProcessingBiometrics = () => {
  const [storeState] = useStoreValue();

  useWaitAndRedirect({
    applicationId: storeState.applicationId,
    testId: 'longPollingWhileProcessingBiometrics',
  });
  const sessionDurationInSec = 2 * import.meta.env.VITE_SESSION_EXPIRATION_IN_SEC;
  return isAU() ? (
    <LayoutPage
      noSessionTimeout
      noTitlePadding
      preHeader={
        <LayoutContent rowsGap="0">
          <SuccessMessage />
        </LayoutContent>
      }
    >
      <SessionTimeout sessionDurationInSec={sessionDurationInSec} />
      <LayoutContent rowsGap="0">
        <BiometricsContent storeState={storeState} />
      </LayoutContent>
    </LayoutPage>
  ) : (
    <LayoutPage noSessionTimeout>
      <SessionTimeout sessionDurationInSec={sessionDurationInSec} />
      <LayoutContent testId="processingBiometrics">
        <BiometricsContent storeState={storeState} />
      </LayoutContent>
    </LayoutPage>
  );
};

const MessageWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  line-height: 24px;
  margin-bottom: 32px;
`;

const NotificationWrapper = styled.div`
  margin-top: 16px;
`;

const ListItem = styled.div`
  display: flex;
  align-items: flex-start;
  border-bottom: 1px solid #d6d6d6;
  padding-bottom: 32px;
  gap: 12px;
  padding-top: 32px;

  &:first-of-type {
    padding-top: 0;
  }

  &:last-of-type {
    border-width: 0;
    padding-bottom: 0;
  }
`;

const TextButton = styled.span`
  color: ${props => props.theme.a.color};
  ${props =>
    props.disabled
      ? `
          cursor: not-allowed;
          text-decoration: none;
        `
      : `
          cursor: pointer;
          text-decoration: underline;`}
`;

const resendFrequencyLimitInSec = 30;

const ResendSmsButton = () => {
  const [storeState] = useStoreValue();
  const { trackEvent } = useEventTracking();
  const { resendBiometricsSms, loading: isResendingBiometricsSms } = useResendBiometricsSms();
  const [isInCoolDown, setIsInCoolDown] = useState(false);
  const [remainingSMSAttempts, setRemainingSMSAttempts] = useState();
  const [attemptsExceeded, setAttemptsExceeded] = useState(false);
  const isDisabled = isResendingBiometricsSms || isInCoolDown;
  const onClick = useCallback(async () => {
    if (isDisabled) {
      return;
    }
    if (remainingSMSAttempts === 0) {
      setAttemptsExceeded(true);
      return;
    }
    trackEvent({
      event: {
        category: 'application',
        action: 'application-navigation',
        location: 'biometrics-processing',
        label: 'resend sms link',
      },
      ...getDataLayerElements(storeState),
    });
    const remainingResendSMSAttempts = await resendBiometricsSms();
    setRemainingSMSAttempts(remainingResendSMSAttempts);
    setIsInCoolDown(true);
    setTimeout(() => {
      setIsInCoolDown(false);
    }, resendFrequencyLimitInSec * 1000);
  }, [isDisabled, remainingSMSAttempts, resendBiometricsSms, storeState, trackEvent]);
  const label = isResendingBiometricsSms ? 'Sending…' : isInCoolDown ? 'SMS sent' : 'Resend SMS';
  return (
    <>
      {!attemptsExceeded && (
        <TextButton
          onClick={onClick}
          disabled={isDisabled}
          style={{
            color: isResendingBiometricsSms ? '#0B2374' : isInCoolDown ? '#20DF5C' : '#0156F5',
          }}
        >
          <strong>{label}</strong>
        </TextButton>
      )}

      {!isInCoolDown && remainingSMSAttempts >= 0 && !attemptsExceeded && (
        <NotificationWrapper>
          <YellowMessage>
            <Message testId="yellow-message" severity="warning-or-significant">
              <Text color={isDJ() && 'indigo'}>You have {remainingSMSAttempts} SMS link attempts remaining.</Text>
            </Message>
          </YellowMessage>
        </NotificationWrapper>
      )}

      {!!attemptsExceeded && (
        <NotificationWrapper>
          <Message testId="exceeded-sms-attempts-message" severity="blocking" bg="secondary.pink.t30">
            <Text color="indigo">
              <strong>You have exceeded the number of SMS attempts.</strong>
            </Text>
            <Text color="indigo">Please contact {config.phoneNumbers.assistance}</Text>
            <Text color="indigo">Your application no. {storeState.applicationRef}</Text>
          </Message>
        </NotificationWrapper>
      )}
    </>
  );
};

const BiometricsContent = ({ storeState }) => {
  return (
    <Stack gap="6">
      <Container>
        <Text>
          {byCountry({
            AU:
              'To fully verify your identity online, we use Biometric verification. We’ve partnered with IDVerse to complete this simple and secure process.',
            NZ:
              'To verify your identity online, we use facial verification. We’ve partnered with IDVerse to make this process simple and secure.',
          })}
        </Text>
        {isAU() && (
          <Flex height="100%" placeItems="center center" margin="8 0 2 0">
            <Stack gap="4">
              <img src={BioIllustration} alt="Biometric verification illustration" />
              <Text>
                <strong>It should take about 3 minutes</strong>
              </Text>
            </Stack>
          </Flex>
        )}
      </Container>
      <Container bg="grey.t03" padding-xs="4" padding-sm="10">
        <ShadowedContainer>
          <Container bg="white" padding="8">
            <ListItem>
              <img src={IconMessage} alt="message icon" />
              <Text>
                <strong>Open the SMS </strong>we've just sent to <strong>{storeState.maskedMobileNumber}</strong> and
                tap on the link.
              </Text>
            </ListItem>
            <ListItem>
              <img src={IconMobile} alt="phone icon" />
              <Text>
                <strong>Follow the prompts on your mobile phone</strong> to complete each step.
              </Text>
            </ListItem>
            <ListItem>
              <img src={IconId} alt="identity icon" />
              <Text>
                <strong>You'll need a government issued photo ID</strong> like a Passport or Drivers Licence.
              </Text>
            </ListItem>
          </Container>
        </ShadowedContainer>
      </Container>
      <Text>
        Haven't received your SMS link or link expired? <ResendSmsButton />
      </Text>

      <Text margin="2 0 4 0">
        <strong>Any further questions please get in touch with us on {config.phoneNumbers.assistance}.</strong>
      </Text>
      <SaveAndCompleteLaterButton infoMessage="If you've completed the Biometric step please wait. This page can take some time to update." />
      {isAU() && (
        <MessageWrapper>
          <img src={IconTimer} alt="Timer icon" />
          <Text>
            Please hold tight once you've completed the Biometric step. This page can take some time to update.
          </Text>
        </MessageWrapper>
      )}
    </Stack>
  );
};
